import React, { useCallback, useEffect, useRef, useState } from "react";
import { App, Avatar, Badge, Button, Modal, Space } from "antd";
import { EmployeesService } from "../../.generated/api-client";
import {
  apiErrorMessage,
  clearAdminToken,
  getAdminToken,
  resetAdminAuthReconnectState,
} from "../services/api";
import { clearStoresCache } from "../hooks/useStoresOptions";
import { AdminQrLoginPanel, useQrLogin } from "./AdminQrLogin";
import AccountMenuTrigger from "./AccountMenuTrigger";
import {
  buildActorBadge,
  buildActorScope,
  buildActorText,
  isUnsupportedScope,
  mapProfile,
} from "./accountConnectModel";
import { type AdminProfile, type AdminScopeStatus } from "./AdminScopeContext";
import "./AccountConnect.css";
import { clearAllReviewDrafts } from "../pages/photo-compliance/reviewDraftStore";
import { clearAdminImpersonationSession } from "../services/impersonationSession";

type Props = {
  reconnectSignal?: number;
  ownerDashboard?: boolean;
  onTokenChange: () => void;
  onScopeChange: (next: {
    profile: AdminProfile | null;
    status: AdminScopeStatus;
    /** status==='error' 时的人话原因 —— 登录墙要把它显给用户 (不然只剩一堵没解释的墙)。 */
    error?: string | null;
  }) => void;
};

/**
 * AccountConnect — 已连接态的账号条 + 「更换账号」弹窗。
 *
 * 断开态不再由本组件承担 (2026-07-11 视觉还原批, 稿 frag-adm-e #ae-login 批注①):
 * scopeStatus 为 disconnected / error 时由 AdminLayout 整屏渲染 AdminLoginWall,
 * 本组件的 Modal 只剩「已登录后更换账号」一个用途。扫码状态机与二维码面板共用
 * AdminQrLogin (单一实现)。
 *
 * 本组件仍必须**始终挂载于已连接壳内** (含驾驶舱把 Header display:none 的情形):
 * 它承载 getMe 范围校验, 不挂载 → 校验永不执行 (rc.13 回归)。
 */
const AccountConnect: React.FC<Props> = ({
  reconnectSignal = 0,
  ownerDashboard = false,
  onTokenChange,
  onScopeChange,
}) => {
  const { message } = App.useApp();
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState<AdminProfile | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [tokenVersion, setTokenVersion] = useState(0);
  const lastReconnectSignalRef = useRef(reconnectSignal);
  const connected = getAdminToken().length > 0;

  // DISC-147: actor 切换时记录上一个 employeeId, 不同 → 清 stores cache 防漏权
  const lastActorEmployeeIdRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!connected) {
      // DISC-147: 断开时清 stores cache (防下个 actor 看到上个 actor 的 cached 全店)
      clearStoresCache();
      lastActorEmployeeIdRef.current = null;
      setProfile(null);
      setProfileError(null);
      setChecking(false);
      onScopeChange({ profile: null, status: "disconnected" });
      return () => {
        cancelled = true;
      };
    }

    setChecking(true);
    setProfileError(null);
    onScopeChange({ profile: null, status: "checking" });
    EmployeesService.getMeEndpointApiV1EmployeesMeGet()
      .then((response) => {
        if (!cancelled) {
          const nextProfile = mapProfile(response);
          // DISC-147: actor 切换 (不同 employee_id) → 清 stores cache
          const prevActorId = lastActorEmployeeIdRef.current;
          if (
            prevActorId !== null &&
            nextProfile.employeeId !== null &&
            prevActorId !== nextProfile.employeeId
          ) {
            clearStoresCache();
          }
          lastActorEmployeeIdRef.current = nextProfile.employeeId;
          setProfile(nextProfile);
          onScopeChange({
            profile: nextProfile,
            status: isUnsupportedScope(nextProfile)
              ? "unsupported_scope"
              : "connected",
          });
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          const reason = apiErrorMessage(error);
          setProfile(null);
          setProfileError(reason);
          onScopeChange({ profile: null, status: "error", error: reason });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setChecking(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [connected, onScopeChange, tokenVersion]);

  const notifyTokenChanged = useCallback(() => {
    setTokenVersion((value) => value + 1);
    onTokenChange();
  }, [onTokenChange]);

  const handleQrAuthenticated = useCallback(() => {
    setOpen(false);
    message.success("账号已连接");
    notifyTokenChanged();
  }, [message, notifyTokenChanged]);

  // 扫码机只在弹窗打开时跑 (关窗即停轮询)。
  const qr = useQrLogin({ enabled: open, onAuthenticated: handleQrAuthenticated });

  useEffect(() => {
    if (
      reconnectSignal <= 0 ||
      reconnectSignal === lastReconnectSignalRef.current
    ) {
      return;
    }
    lastReconnectSignalRef.current = reconnectSignal;
    message.warning("登录状态已变化，请重新扫码登录。");
  }, [message, reconnectSignal]);

  /**
   * actor 卡视觉收口 (DISC-NNN): 把原来 6 块独立 tag 收成 1 个紧凑 actor 卡 + 2 个小按钮.
   */
  const actorBadge = buildActorBadge({
    connected,
    checking,
    profileError,
    profile,
  });
  const actorText = buildActorText({
    connected,
    checking,
    profileError,
    profile,
  });

  const disconnect = useCallback(() => {
    clearAllReviewDrafts();
    clearStoresCache();
    lastActorEmployeeIdRef.current = null;
    clearAdminToken();
    clearAdminImpersonationSession();
    resetAdminAuthReconnectState();
    setProfile(null);
    setProfileError(null);
    onScopeChange({ profile: null, status: "disconnected" });
    message.success("已断开账号连接");
    notifyTokenChanged();
  }, [message, notifyTokenChanged, onScopeChange]);

  const roleLabel = profile?.isSuperAdmin ? "超级管理者" : profile?.positionCn || "角色待确认";
  const scopeLabel = buildActorScope(profile) || "范围待确认";
  const avatarLabel = profile?.fullName?.trim().slice(0, 1) || "账";
  const accountCard = (
    <div className="account-popover" data-testid="account-popover">
      <div className="account-popover__identity">
        <Avatar className="account-popover__avatar">{avatarLabel}</Avatar>
        <div className="account-popover__identity-copy">
          <strong className="account-popover__name">{profile?.fullName || "已连接账号"}</strong>
          <span className="account-popover__role">{roleLabel}</span>
        </div>
      </div>
      <dl className="account-popover__facts">
        <div>
          <dt>可见范围</dt>
          <dd>{scopeLabel}</dd>
        </div>
        <div>
          <dt>连接状态</dt>
          <dd className="account-popover__connected"><Badge status={actorBadge} />已连接</dd>
        </div>
      </dl>
      <div className="account-popover__actions">
        <Button size="small" onClick={() => setOpen(true)}>更换账号</Button>
        <Button size="small" type="text" danger onClick={disconnect}>断开</Button>
      </div>
    </div>
  );
  return (
    <>
      <AccountMenuTrigger
        actorText={actorText}
        avatarLabel={avatarLabel}
        hasProfile={Boolean(profile)}
        content={accountCard}
        portalSelector={ownerDashboard ? '[data-admin-account-slot="owner-dashboard"]' : undefined}
      />
      {/* 「更换账号」弹窗 —— 断开态已经由整屏登录墙接管, 这里只剩"已登录后换个账号"。
          二维码 / 状态机 / 临时凭证入口全部复用 AdminQrLoginPanel, 不再有第二份实现。 */}
      <Modal
        title="更换账号"
        open={open}
        okText="刷新二维码"
        cancelText="取消"
        onCancel={() => {
          setOpen(false);
          resetAdminAuthReconnectState();
        }}
        onOk={() => qr.restart()}
      >
        <Space direction="vertical" size={14} className="drawer-stack">
          <AdminQrLoginPanel qr={qr} variant="modal" />
        </Space>
      </Modal>
    </>
  );
};

export default AccountConnect;
